import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Star, Loader2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewModal = ({ isOpen, onClose, wedding, reviewedUser, token, onSuccess }) => {
  const { t } = useLanguage();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error(t('review.selectRating') || 'Please select a rating');
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API_URL}/api/reviews`,
        {
          reviewed_id: reviewedUser.id,
          wedding_id: wedding.id,
          rating,
          comment,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      toast.success(t('review.success') || 'Review submitted successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('review.error') || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRating(0);
    setHoverRating(0);
    setComment('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-primary" />
            {t('review.title') || 'Write a Review'}
          </DialogTitle>
          <DialogDescription>
            {t('review.description') || 'Share your experience with'} {reviewedUser?.name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Star Rating */}
          <div className="flex flex-col items-center gap-2">
            <p className="text-sm text-muted-foreground">
              {t('review.ratingLabel') || 'How would you rate your experience?'}
            </p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`h-8 w-8 transition-colors ${
                      star <= (hoverRating || rating)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm font-medium">
              {rating === 1 && (t('review.rating1') || 'Poor')}
              {rating === 2 && (t('review.rating2') || 'Fair')}
              {rating === 3 && (t('review.rating3') || 'Good')}
              {rating === 4 && (t('review.rating4') || 'Very Good')}
              {rating === 5 && (t('review.rating5') || 'Excellent')}
            </p>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('review.commentLabel') || 'Your Review'} ({t('review.optional') || 'optional'})
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('review.commentPlaceholder') || 'Tell others about your experience...'}
              rows={4}
              className="resize-none"
            />
          </div>

          {/* Wedding Info */}
          <div className="bg-secondary/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground">{t('review.for') || 'Review for'}</p>
            <p className="font-medium">{wedding?.title}</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button type="submit" disabled={loading || rating === 0} className="flex-1">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading') || 'Loading...'}
                </>
              ) : (
                t('review.submit') || 'Submit Review'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
